import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowBack, MdDownload, MdFileDownload, MdRefresh } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { Button } from "rsuite";
import * as XLSX from "xlsx";
import axios from "axios";
import { web_url } from "../../constant";

function PendingLetters() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pendingLetters, setPendingLetters] = useState([]);
    const [openError, setOpenError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [expandedIds, setExpandedIds] = useState({});

    const handleCloseError = () => {
        setOpenError(false);
    };

    const toggleExpand = (id) => {
        setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const getToken = () => {
        const logDetails =
            JSON.parse(localStorage.getItem("logDetails")) ||
            JSON.parse(sessionStorage.getItem("logDetails"));
        return logDetails?.token;
    };

    const formatDateTime = (ts) => {
        if (!ts) return "—";
        return new Date(ts).toLocaleString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const stripHtml = (html) =>
        (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

    const fetchPendingLetters = (isRefresh = false) => {
        const token = getToken();
        if (!token) return;
        isRefresh ? setIsRefreshing(true) : setIsLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };
        axios.get(`${web_url}/api/email-threads/pending-letters`, config)
            .then((res) => {
                setPendingLetters(res.data || []);
            })
            .catch(() => {
                setErrorMsg("Failed to load pending letters. Please try again.");
                setOpenError(true);
            })
            .finally(() => {
                isRefresh ? setIsRefreshing(false) : setIsLoading(false);
            });
    };


    useEffect(() => {
        fetchPendingLetters(false);
    }, []);

    const getReceiver = (msg) => {
        const participants = msg.threadParticipants || [];
        const receiver = participants.find((p) => p !== msg.sender);
        return receiver || "—";
    };

    const exportExcel = () => {
        if (pendingLetters.length === 0) return;
        const wb = XLSX.utils.book_new();
        const rows = pendingLetters.map((msg, i) => ({
            "#": i + 1,
            "To": getReceiver(msg),
            "From": msg.sender || "—",
            "Date & Time Sent": formatDateTime(msg.sentAt),
            "Status": "Pending",
            "Original Letter": stripHtml(msg.body) || "—",
            "English Version": stripHtml(msg.englishBody) || "—",
            "Translated Version": stripHtml(msg.translatedBody) && msg.translatedBody !== "NA" ? stripHtml(msg.translatedBody) : "—",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);

        const colWidths = [
            { wch: 4 },
            { wch: 32 },
            { wch: 32 },
            { wch: 20 },
            { wch: 15 },
            { wch: 60 },
            { wch: 60 },
            { wch: 60 },
        ];
        ws["!cols"] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, "Pending Letters");
        XLSX.writeFile(wb, `pending_letters_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const downloadPdf = () => {
        if (pendingLetters.length === 0) return;

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            setErrorMsg("Please allow popups to download the PDF.");
            setOpenError(true);
            return;
        }
        printWindow.document.write(
            "<html><head><title>Loading...</title></head><body><h2 style='font-family:sans-serif;text-align:center;margin-top:50px;color:#217346;'>Generating Report...</h2></body></html>"
        );

        let bodyContent = "";
        pendingLetters.forEach((msg, i) => {
            const receiver = getReceiver(msg);
            const originalText = stripHtml(msg.body);
            const englishText = stripHtml(msg.englishBody);
            const translatedText = msg.translatedBody && msg.translatedBody !== "NA" ? stripHtml(msg.translatedBody) : "";

            bodyContent += `
                <div class="letter" style="${i > 0 ? "page-break-before: always;" : ""}">
                    <h2 style="font-size:16px;color:#217346;margin-bottom:8px;border-bottom:2px solid #A1E0BB;padding-bottom:6px;">Pending Letter ${i + 1}</h2>
                    <table style="font-size:12px;margin-bottom:12px;border-collapse:collapse;width:100%;">
                        <tr style="border-bottom:1px solid #eee;">
                            <td style="padding:6px 12px 6px 0;font-weight:bold;color:#555;width:200px;">To:</td>
                            <td style="padding:6px 0;">${receiver}</td>
                        </tr>
                        <tr style="border-bottom:1px solid #eee;">
                            <td style="padding:6px 12px 6px 0;font-weight:bold;color:#555;">From:</td>
                            <td style="padding:6px 0;">${msg.sender || "—"}</td>
                        </tr>
                        <tr style="border-bottom:1px solid #eee;">
                            <td style="padding:6px 12px 6px 0;font-weight:bold;color:#555;">Date &amp; Time Sent:</td>
                            <td style="padding:6px 0;">${formatDateTime(msg.sentAt)}</td>
                        </tr>
                        <tr>
                            <td style="padding:6px 12px 6px 0;font-weight:bold;color:#555;">Status:</td>
                            <td style="padding:6px 0;color:#217346;font-weight:600;">Pending</td>
                        </tr>
                    </table>
                    ${originalText ? `
                    <div style="margin-top:14px;">
                        <p style="font-size:12px;font-weight:bold;color:#1A5276;margin-bottom:6px;border-left:3px solid #1A5276;padding-left:8px;">Original Letter</p>
                        <div style="font-size:12px;background:#F4F8FC;border:1px solid #D6E4F0;border-radius:4px;padding:12px 16px;line-height:1.8;white-space:pre-wrap;">${originalText}</div>
                    </div>` : ""}
                    ${englishText ? `
                    <div style="margin-top:14px;">
                        <p style="font-size:12px;font-weight:bold;color:#217346;margin-bottom:6px;border-left:3px solid #217346;padding-left:8px;">English Version</p>
                        <div style="font-size:12px;background:#F0FBF4;border:1px solid #B7DFC5;border-radius:4px;padding:12px 16px;line-height:1.8;white-space:pre-wrap;">${englishText}</div>
                    </div>` : ""}
                    ${translatedText ? `
                    <div style="margin-top:14px;">
                        <p style="font-size:12px;font-weight:bold;color:#856404;margin-bottom:6px;border-left:3px solid #856404;padding-left:8px;">Translated Version</p>
                        <div style="font-size:12px;background:#FFFDF0;border:1px solid #FFEBAA;border-radius:4px;padding:12px 16px;line-height:1.8;white-space:pre-wrap;">${translatedText}</div>
                    </div>` : ""}
                </div>
            `;
        });

        const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Pending Letters</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Sinhala&family=Noto+Sans+Tamil&family=Noto+Sans&display=swap');
body { font-family: 'Noto Sans', 'Noto Sans Sinhala', 'Noto Sans Tamil', Arial, sans-serif; padding: 20px; color: #333; }
.letter { page-break-inside: avoid; margin-bottom: 30px; }
</style></head><body>
<h1 style="font-size:22px;color:#217346;border-bottom:2px solid #A1E0BB;padding-bottom:10px;">Pending Letters</h1>
<p style="font-size:13px;color:#666;">Total pending: ${pendingLetters.length} | Generated: ${new Date().toLocaleString()}</p>
<hr style="border:1px solid #eee;margin:20px 0;" />
${bodyContent}
<script>setTimeout(function(){ window.print(); }, 500);<\/script>
</body></html>`;

        printWindow.document.open();
        printWindow.document.write(fullHtml);
        printWindow.document.close();
    };

    return (
        <>
            <div className={`${i18n.language === "en" ? "font-urban" : i18n.language === "si" ? "font-noto" : "font-mukta"} overflow-x-hidden xl:pt-[20px] min-h-screen pb-[120px]`}>
                <div className="pt-[100px] md:pt-[130px] lg:pt-[150px] xl:pt-[0px] w-full flex justify-between items-center pr-[20px] md:pr-[60px] xl:pr-[80px]">
                    <div className="ml-[30px] md:ml-[60px] lg:ml-[80px] xl:ml-[80px] flex items-center">
                        <MdArrowBack size={25} className="md:hidden cursor-pointer" onClick={() => navigate("/mobile/researcher/dashboard")} />
                        <MdArrowBack size={30} className="hidden md:block xl:hidden cursor-pointer" onClick={() => navigate("/mobile/researcher/dashboard")} />
                        <MdArrowBack size={30} className="hidden xl:block cursor-pointer" onClick={() => navigate("/desktop/researcher/dashboard")} />
                        <p className="text-[20px] md:text-[23px] lg:text-[25px] xl:text-[20px] 2xl:text-[22px] font-bold px-[10px] py-[5px] md:px-[13px] md:py-[8px]">
                            Pending Letters
                        </p>
                    </div>
                    <button
                        id="refresh-pending-btn"
                        disabled={isRefreshing}
                        onClick={() => fetchPendingLetters(true)}
                        className="flex items-center gap-[6px] bg-[#FDC50C] text-black rounded-md text-[13px] md:text-[15px] py-[5px] px-[10px] hover:bg-opacity-80 disabled:opacity-50"
                    >
                        <MdRefresh size={18} className={isRefreshing ? "animate-spin" : ""} />
                        Refresh
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center pt-[60px]">
                        <div className="w-10 h-10 border-2 border-t-transparent border-[#FFA803] rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[20px]">
                            <div className="flex items-center justify-between mb-[16px]">
                                <div className="bg-[#E8F8F0] border border-[#A1E0BB] rounded-xl px-[20px] py-[12px] flex items-center gap-[12px]">
                                    <p className="text-[14px] md:text-[16px] text-gray-600 font-medium">Total Pending Letters:</p>
                                    <p className="text-[22px] md:text-[26px] font-bold text-[#217346]">{pendingLetters.length}</p>
                                </div>
                                {pendingLetters.length > 0 && (
                                    <div className="flex flex-wrap gap-[10px]">
                                        <button
                                            id="download-pending-excel-btn"
                                            onClick={exportExcel}
                                            className="flex items-center gap-[8px] bg-[#217346] text-white rounded-md text-[13px] md:text-[15px] py-[8px] px-[14px] hover:bg-opacity-90"
                                        >
                                            <MdDownload size={18} />
                                            Download Excel
                                        </button>
                                        <button
                                            id="download-pending-pdf-btn"
                                            onClick={downloadPdf}
                                            className="flex items-center gap-[8px] bg-[#336C4B] text-white rounded-md text-[13px] md:text-[15px] py-[8px] px-[14px] hover:bg-opacity-90"
                                        >
                                            <MdFileDownload size={18} />
                                            Download PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {pendingLetters.length === 0 ? (
                            <div className="flex justify-center items-center pt-[60px]">
                                <p className="text-gray-500 text-[16px]">No pending letters found.</p>
                            </div>
                        ) : (
                            <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[8px] flex flex-col gap-[14px]">
                                {pendingLetters.map((msg, i) => {
                                    const isExpanded = !!expandedIds[msg.messageId || i];
                                    const originalText = stripHtml(msg.body);
                                    const englishText = stripHtml(msg.englishBody);
                                    const translatedText = msg.translatedBody && msg.translatedBody !== "NA" ? stripHtml(msg.translatedBody) : "";
                                    const hasContent = originalText || englishText || translatedText;

                                    return (
                                        <div
                                            key={msg.messageId || i}
                                            className="bg-white border border-[#A1E0BB] rounded-xl shadow-sm overflow-hidden"
                                        >
                                            <div className="bg-[#DBFAE8] px-[20px] py-[10px] flex items-center justify-between">
                                                <p className="font-bold text-[14px] md:text-[15px] text-[#336C4B]">
                                                    Pending Letter {i + 1}
                                                </p>
                                                <span className="bg-[#336C4B] text-white text-[11px] font-semibold px-[8px] py-[3px] rounded-full">
                                                    PENDING
                                                </span>
                                            </div>

                                            <div className="px-[20px] py-[16px] grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                                                <div className="flex flex-col gap-[2px]">
                                                    <p className="text-[11px] md:text-[12px] font-semibold text-gray-500 uppercase tracking-wide">To</p>
                                                    <p className="text-[13px] md:text-[14px] text-gray-800 break-all">{getReceiver(msg)}</p>
                                                </div>
                                                <div className="flex flex-col gap-[2px]">
                                                    <p className="text-[11px] md:text-[12px] font-semibold text-gray-500 uppercase tracking-wide">From</p>
                                                    <p className="text-[13px] md:text-[14px] text-gray-800 break-all">{msg.sender || "—"}</p>
                                                </div>
                                                <div className="flex flex-col gap-[2px]">
                                                    <p className="text-[11px] md:text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Date &amp; Time Sent</p>
                                                    <p className="text-[13px] md:text-[14px] text-gray-800">{formatDateTime(msg.sentAt)}</p>
                                                </div>

                                            </div>

                                            {hasContent && (
                                                <div className="border-t border-[#A1E0BB]">
                                                    <button
                                                        onClick={() => toggleExpand(msg.messageId || i)}
                                                        className="w-full flex items-center justify-between px-[20px] py-[10px] bg-[#F0FBF4] hover:bg-[#e0f7e9] transition-colors text-[13px] font-semibold text-[#336C4B]"
                                                    >
                                                        <span>{isExpanded ? "Hide Letter Content" : "View Letter Content"}</span>
                                                        <span className="text-[16px]">{isExpanded ? "▲" : "▼"}</span>
                                                    </button>

                                                    {isExpanded && (
                                                        <div className="px-[20px] pb-[20px] flex flex-col gap-[14px]">
                                                            {originalText && (
                                                                <div>
                                                                    <p className="text-[11px] md:text-[12px] font-semibold text-[#1A5276] uppercase tracking-wide mb-[6px] border-l-[3px] border-[#1A5276] pl-[8px]">
                                                                        Original Letter
                                                                    </p>
                                                                    <div className="bg-[#F4F8FC] border border-[#D6E4F0] rounded-lg px-[14px] py-[12px] text-[13px] md:text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                                                                        {originalText}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {englishText && (
                                                                <div>
                                                                    <p className="text-[11px] md:text-[12px] font-semibold text-[#217346] uppercase tracking-wide mb-[6px] border-l-[3px] border-[#217346] pl-[8px]">
                                                                        English Version
                                                                    </p>
                                                                    <div className="bg-[#F0FBF4] border border-[#B7DFC5] rounded-lg px-[14px] py-[12px] text-[13px] md:text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                                                                        {englishText}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {translatedText && (
                                                                <div>
                                                                    <p className="text-[11px] md:text-[12px] font-semibold text-[#856404] uppercase tracking-wide mb-[6px] border-l-[3px] border-[#856404] pl-[8px]">
                                                                        Translated Version
                                                                    </p>
                                                                    <div className="bg-[#FFFDF0] border border-[#FFEBAA] rounded-lg px-[14px] py-[12px] text-[13px] md:text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                                                                        {translatedText}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            <Dialog
                open={openError}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") handleCloseError();
                }}
                aria-labelledby="pending-error-title"
                aria-describedby="pending-error-desc"
                className="backdrop-blur-sm"
            >
                <DialogTitle id="pending-error-title" className="text-2xl font-bold text-gray-800 border-b border-gray-300 pb-2">
                    Error
                </DialogTitle>
                <DialogContent className="bg-gray-50">
                    <DialogContentText id="pending-error-desc" className="text-gray-600 text-md mt-2">
                        {errorMsg}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="px-4 py-2">
                    <Button
                        onClick={handleCloseError}
                        style={{ backgroundColor: "#F2AC0E", color: "white", fontWeight: "bold" }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PendingLetters;
